/**
 * The editable rail (browser tab and root app): starred rows, user-named
 * sections and spaces in the user's order. Rows drag anywhere — within a
 * section, into or out of one, among a parent's sub-items; a section drags by
 * its header and carries its rows; a space drags in edit mode. Sections, spaces
 * and sub-items are made in edit mode only (`useRailEditStore`), entered from any
 * rail right-click menu. Every change is one whole-layout write through `useStars`.
 *
 * Spec: docs/plans/2026-09-16-nav-rail-sections-design.md §2 (revisions 2 and 3).
 */
import React, { useState } from 'react';

import { DndContext } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';

import {
  idOf,
  isSection,
  isSpace,
  NEW_SECTION_LABEL,
  ROOT,
  type RailLayout,
} from '@shared/navigation/railLayout';
import { addSection, addSpace, renameSection } from '@shared/navigation/railLayoutOps';
import { useStars } from '@shared/navigation/useStars';

import { RailEntryView } from './RailEntryView';
import { SectionBlock, type SectionActions } from './SectionBlock';
import { SpaceItem } from './SpaceItem';
import { railCollision, useRailDrag } from './useRailDrag';
import { useRailEditStore } from './useRailEditStore';

import type { NavCounts } from '@app/stores/slices/uiSlice';

interface SectionedRailProps {
  isCollapsed: boolean;
  counts: NavCounts;
  rowClick: { onClick?: () => void };
}

/** The rail's write actions, bound to the current layout. */
function useRailActions(): {
  layout: RailLayout;
  actions: SectionActions;
  renamingId: string | null;
} {
  const { layout, setLayout, toggle } = useStars();
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const commit = (next: RailLayout) => void setLayout(next);
  const actions: SectionActions = {
    commit,
    unpin: id => void toggle(id),
    startSection: beforeId => {
      const added = addSection(layout, NEW_SECTION_LABEL, beforeId);
      commit(added.layout);
      setRenamingId(added.id);
    },
    addSpace: beforeId => commit(addSpace(layout, beforeId)),
    startRename: setRenamingId,
    rename: (id, label) => {
      setRenamingId(null);
      const next = renameSection(layout, id, label);
      if (JSON.stringify(next) !== JSON.stringify(layout)) commit(next);
    },
    cancelRename: () => setRenamingId(null),
    startEditing: () => useRailEditStore.getState().setEditing(true),
  };
  return { layout, actions, renamingId };
}

const ADD_BUTTON_CLASS =
  'text-sidebar-foreground/60 hover:bg-sidebar-accent hover:text-sidebar-foreground flex h-6 items-center rounded px-2 text-left text-xs';

/** The editable, sectioned rail. */
const SectionedRail: React.FC<SectionedRailProps> = ({ isCollapsed, counts, rowClick }) => {
  const { layout, actions, renamingId } = useRailActions();
  const editMode = useRailEditStore(s => s.editing);
  const { sensors, items, handlers } = useRailDrag(layout, actions.commit);
  const ctx = { layout, isCollapsed, editMode, counts, rowClick, actions };

  return (
    <DndContext sensors={sensors} collisionDetection={railCollision} {...handlers}>
      {layout.length === 0 && !isCollapsed && (
        <p className="text-sidebar-foreground/60 px-3 py-2 text-xs leading-snug">
          Nothing pinned. Star anything in All areas and it appears here.
        </p>
      )}
      <SortableContext items={items.map(idOf)} strategy={verticalListSortingStrategy}>
        {items.map(item => {
          if (isSection(item)) {
            return (
              <SectionBlock
                key={item.id}
                section={item}
                renaming={renamingId === item.id}
                {...ctx}
              />
            );
          }
          if (isSpace(item)) {
            return <SpaceItem key={item.id} space={item} {...ctx} />;
          }
          return <RailEntryView key={idOf(item)} entry={item} container={ROOT} ctx={ctx} />;
        })}
      </SortableContext>
      {editMode && !isCollapsed && (
        <div className="mt-1.5 flex gap-1">
          <button type="button" onClick={() => actions.startSection()} className={ADD_BUTTON_CLASS}>
            ＋&nbsp; New section
          </button>
          <button type="button" onClick={() => actions.addSpace()} className={ADD_BUTTON_CLASS}>
            ＋&nbsp; Space
          </button>
        </div>
      )}
    </DndContext>
  );
};

export default SectionedRail;
